import axios from 'axios';

// In production (Railway), REACT_APP_API_URL is set to the backend service URL
// e.g. https://mgsg-api.up.railway.app
// In development, CRA proxy (package.json "proxy") forwards /api → localhost:5000
const BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

export const api = axios.create({ baseURL: BASE_URL });

// Attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Tell the server which hat a dual Admin/Partner user is wearing, so the API
  // enforces the same view the screens show (see middleware/auth.ts).
  config.headers['X-View-Mode'] = localStorage.getItem('viewMode') === 'admin' ? 'admin' : 'partner';
  return config;
});

// On 401 — token expired or invalid → clear session and go to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Never bounce out of the platform console: an operator can have a stale
      // firm token sitting in the same browser, and expiring it must not throw
      // them out of a session that has nothing to do with it.
      const inSuperConsole = window.location.pathname.startsWith('/super');
      // Use replace so the user can't press Back into a protected page
      if (window.location.pathname !== '/login' && !inSuperConsole) {
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Staff
export const getStaff = () => api.get('/staff');
export const createStaff = (data: any) => api.post('/staff', data);
export const updateStaff = (id: number, data: any) => api.put(`/staff/${id}`, data);
export const deleteStaff = (id: number) => api.delete(`/staff/${id}`);
export const toggleStaffActive = (id: number) => api.put(`/staff/${id}/toggle-active`);
export const resetStaffPassword = (id: number, newPassword: string) => api.post(`/staff/${id}/reset-password`, { newPassword });

// Clients
export const getClients = (search?: string) => api.get('/clients', { params: search ? { search } : {} });
export const getReferringCas = () => api.get('/clients/referring-cas');
export const createClient = (data: any) => api.post('/clients', data);
export const updateClient = (id: number, data: any) => api.put(`/clients/${id}`, data);
export const deleteClient = (id: number) => api.delete(`/clients/${id}`);
export const getClientProfile = (id: number) => api.get(`/clients/${id}/profile`);

// Tasks
// No params → legacy full array. With { page, pageSize, ...filters } →
// server-side paged response { rows, total, page, pageSize }.
export const getTasks = (params?: any) => api.get('/tasks', { params });
export const getTaskFilterOptions = () => api.get('/tasks/filter-options');
export const getTask = (id: number) => api.get(`/tasks/${id}`);
export const createTask = (data: any) => api.post('/tasks', data);
export const updateTask = (id: number, data: any) => api.put(`/tasks/${id}`, data);
export const deleteTask = (id: number) => api.delete(`/tasks/${id}`);
export const addExpense = (taskId: number, data: any) => api.post(`/tasks/${taskId}/expenses`, data);
export const deleteExpense = (id: number) => api.delete(`/tasks/expenses/${id}`);
// Reveal-and-audit: fetch the (decrypted) archive link for a task. Logged
// server-side; triggers anomaly alerts for non-admin power users.
export const revealTaskArchiveLink = (id: number) => api.get<{ url: string }>(`/tasks/${id}/archive-link`);

// Certificates
export const getCertificates = (taskId: number) => api.get(`/tasks/${taskId}/certificates`);
export const createCertificate = (taskId: number, data: any) => api.post(`/tasks/${taskId}/certificates`, data);
export const updateCertificate = (taskId: number, id: number, data: any) => api.put(`/tasks/${taskId}/certificates/${id}`, data);
export const deleteCertificate = (taskId: number, id: number) => api.delete(`/tasks/${taskId}/certificates/${id}`);
export const getAllCertificates = (params?: Record<string, any>) => api.get('/certificates', { params });
export const uploadCertificateFile = (id: number, file: File) => {
  const fd = new FormData(); fd.append('file', file);
  return api.post(`/certificates/${id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteCertificateFile = (id: number) => api.delete(`/certificates/${id}/file`);
export const revealCertificateArchiveLink = (id: number) => api.get<{ url: string }>(`/certificates/${id}/archive-link`);

// Compliance types (masters)
export const getComplianceTypes = () => api.get('/masters/compliance-types');
export const createComplianceType = (data: any) => api.post('/masters/compliance-types', data);
export const updateComplianceType = (id: number, data: any) => api.put(`/masters/compliance-types/${id}`, data);
export const deleteComplianceType = (id: number) => api.delete(`/masters/compliance-types/${id}`);

// Task compliance enrollment + instances
export const getTaskEnrollments = (taskId: number) => api.get(`/tasks/${taskId}/compliance-types`);
export const enrollComplianceType = (taskId: number, data: any) => api.post(`/tasks/${taskId}/compliance-types`, data);
export const removeEnrollment = (taskId: number, id: number) => api.delete(`/tasks/${taskId}/compliance-types/${id}`);
export const getTaskCompliances = (taskId: number) => api.get(`/tasks/${taskId}/compliances`);

// Global compliance list + file operations
export const getAllCompliances = (params?: Record<string, any>) => api.get('/compliances', { params });
export const updateCompliance = (id: number, data: any) => api.put(`/compliances/${id}`, data);
export const deleteCompliance = (id: number) => api.delete(`/compliances/${id}`);
export const uploadComplianceFile = (id: number, file: File) => {
  const fd = new FormData(); fd.append('file', file);
  return api.post(`/compliances/${id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteComplianceFile = (id: number) => api.delete(`/compliances/${id}/file`);
export const triggerComplianceCron = () => api.post('/compliances/generate');
export const bulkUpdateCompliances = (data: { ids: number[]; action: string; status?: string; extendedDueDate?: string }) =>
  api.post('/compliances/bulk', data);
export const bulkEnrollCompliances = (data: {
  complianceTypeId: number;
  rows: Array<{ taskId: number; taxId: string; startPeriod: string; endPeriod?: string }>;
}) => api.post('/compliances/bulk-enroll', data);
export const triggerGstFilingCheck = (filters?: { typeNames?: string[]; periods?: string[] }) =>
  api.post('/compliances/gst-filing-check', filters || {});
export const getGstComplianceSummary = () => api.get('/compliances/gst-summary');

export const getTaskStaff = (taskId: number) => api.get(`/tasks/${taskId}/staff`);
export const addTaskStaff = (taskId: number, staffId: number) => api.post(`/tasks/${taskId}/staff`, { staffId });
export const addTaskTeam = (taskId: number, partnerId: number) => api.post(`/tasks/${taskId}/staff/team`, { partnerId });
export const removeTaskStaff = (taskId: number, staffId: number) => api.delete(`/tasks/${taskId}/staff/${staffId}`);

// Timesheets
export const getWeeklyTimesheet = (staffId?: number, weekStart?: string) =>
  api.get('/timesheets', { params: { staffId, weekStart } });
export const getAllTimesheets = (weekStart: string) =>
  api.get('/timesheets/all', { params: { weekStart } });
export const upsertTimesheetEntry = (data: any) => api.post('/timesheets', data);
export const updateTimesheetDescription = (data: { staffId?: number; taskId: number; weekStart: string; description: string }) =>
  api.put('/timesheets/description', data);
export const deleteTimesheetEntry = (id: number) => api.delete(`/timesheets/${id}`);

// Auth
export const updatePassword = (data: any) => api.put('/auth/password', data);
export const getProfile = () => api.get('/auth/profile');

// Masters
export const getProfitCentres = () => api.get('/masters/profit-centres');
export const createProfitCentre = (data: any) => api.post('/masters/profit-centres', data);
export const updateProfitCentre = (id: number, data: any) => api.put(`/masters/profit-centres/${id}`, data);
export const deleteProfitCentre = (id: number) => api.delete(`/masters/profit-centres/${id}`);
export const assignPartnerToProfitCentre = (pcId: number, staffId: number) =>
  api.post(`/masters/profit-centres/${pcId}/partners`, { staffId });
export const removePartnerFromProfitCentre = (pcId: number, staffId: number) =>
  api.delete(`/masters/profit-centres/${pcId}/partners/${staffId}`);

export const getCategories = () => api.get('/masters/categories');
export const createCategory = (data: any) => api.post('/masters/categories', data);
export const updateCategory = (id: number, data: any) => api.put(`/masters/categories/${id}`, data);
export const deleteCategory = (id: number) => api.delete(`/masters/categories/${id}`);

export const getBillingEntities = () => api.get('/masters/billing-entities');
export const createBillingEntity = (data: any) => api.post('/masters/billing-entities', data);
export const updateBillingEntity = (id: number, data: any) => api.put(`/masters/billing-entities/${id}`, data);
export const deleteBillingEntity = (id: number) => api.delete(`/masters/billing-entities/${id}`);

export const getSignatories = (billingEntityId?: number) =>
  api.get('/masters/signatories', { params: billingEntityId ? { billingEntityId } : {} });
export const createSignatory = (data: FormData) =>
  api.post('/masters/signatories', data, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateSignatory = (id: number, data: FormData) =>
  api.put(`/masters/signatories/${id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteSignatory = (id: number) => api.delete(`/masters/signatories/${id}`);

/**
 * Builds the URL for a file stored in the server's uploads folder.
 *
 * /uploads is authenticated and ACL-checked per record. Because <img src> and
 * <a href> cannot carry an Authorization header, the file token goes in the
 * query string — it is scoped to file reads only and is rejected as a session
 * token. Always route file links through this helper; a bare /uploads/... path
 * will 401.
 */
export const uploadsUrl = (filename: string): string => {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const segments = String(filename).split('/').map(encodeURIComponent).join('/');
  const fileToken = localStorage.getItem('fileToken');
  return `${base}/uploads/${segments}${fileToken ? `?ft=${encodeURIComponent(fileToken)}` : ''}`;
};

// Tickets
export const getTickets = (params?: { category?: 'IT' | 'ADMIN' }) => api.get('/tickets', { params });
export const getTicket = (id: number) => api.get(`/tickets/${id}`);
export const createTicket = (data: any) => api.post('/tickets', data);
export const updateTicket = (id: number, data: any) => api.put(`/tickets/${id}`, data);
export const deleteTicket = (id: number) => api.delete(`/tickets/${id}`);
export const assignTicket = (id: number, data: any) => api.put(`/tickets/${id}/assign`, data);
export const requestCostApproval = (id: number, data: any) => api.put(`/tickets/${id}/request-approval`, data);
export const approveTicketCost = (id: number) => api.put(`/tickets/${id}/approve-cost`);
export const rejectTicketCost = (id: number, data: any) => api.put(`/tickets/${id}/reject-cost`, data);
export const resolveTicket = (id: number, data: any) => api.put(`/tickets/${id}/resolve`, data);
export const closeTicket = (id: number, data?: { resolutionNotes?: string }) => api.put(`/tickets/${id}/close`, data ?? {});
export const reopenTicket = (id: number) => api.put(`/tickets/${id}/reopen`);
export const addTicketComment = (id: number, data: any) => api.post(`/tickets/${id}/comments`, data);
export const deleteTicketAttachment = (id: number) => api.delete(`/tickets/attachments/${id}`);

// Reimbursements
export const getReimbursements = () => api.get('/reimbursements');
export const getReimbursement = (id: number) => api.get(`/reimbursements/${id}`);
export const createReimbursement = (data: any) => api.post('/reimbursements', data);
export const updateReimbursement = (id: number, data: any) => api.put(`/reimbursements/${id}`, data);
export const deleteReimbursement = (id: number) => api.delete(`/reimbursements/${id}`);
export const addReimbursementItem = (id: number, data: any) => api.post(`/reimbursements/${id}/items`, data);
export const updateReimbursementItem = (itemId: number, data: any) => api.put(`/reimbursements/items/${itemId}`, data);
export const deleteReimbursementItem = (itemId: number) => api.delete(`/reimbursements/items/${itemId}`);
export const uploadReimbursementAttachment = (itemId: number, formData: FormData) =>
  api.post(`/reimbursements/items/${itemId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteReimbursementAttachment = (attId: number) => api.delete(`/reimbursements/attachments/${attId}`);
export const reviewReimbursement = (id: number) => api.put(`/reimbursements/${id}/review`);
export const returnReimbursement = (id: number, data: any) => api.put(`/reimbursements/${id}/return`, data);
export const resubmitReimbursement = (id: number) => api.put(`/reimbursements/${id}/resubmit`);
export const reopenReimbursement = (id: number) => api.put(`/reimbursements/${id}/reopen`);
export const approveReimbursement = (id: number) => api.put(`/reimbursements/${id}/approve`);
export const rejectReimbursement = (id: number, data: any) => api.put(`/reimbursements/${id}/reject`, data);
export const markReimbursementPaid = (id: number, data: { paidOn: string; paymentMode?: string; paymentReference?: string }) =>
  api.put(`/reimbursements/${id}/mark-paid`, data);
export const bulkUpdateReimbursements = (data: { ids: number[]; action: string; rejectionReason?: string; paidOn?: string; paymentMode?: string; paymentReference?: string }) =>
  api.post('/reimbursements/bulk', data);
export const exportReimbursements = (params?: { status?: string; search?: string; submittedFrom?: string; submittedTo?: string }) =>
  api.get('/reimbursements/export', { params, responseType: 'blob' });
export const getExpenseCategories = () => api.get('/reimbursements/categories/active');
export const getAllExpenseCategories = () => api.get('/reimbursements/categories/all');
export const createExpenseCategory = (data: any) => api.post('/reimbursements/categories', data);
export const updateExpenseCategory = (id: number, data: any) => api.put(`/reimbursements/categories/${id}`, data);
export const deleteExpenseCategory = (id: number) => api.delete(`/reimbursements/categories/${id}`);

// Invoices
export const getInvoices = (params?: any) => api.get('/invoices', { params });
export const getInvoiceSummary = (params?: any) => api.get('/invoices/summary', { params });
export const getInvoice = (id: number) => api.get(`/invoices/${id}`);
export const getReceivables = () => api.get('/invoices/receivables');
// Collections follow-up remarks (invoice or opening-balance receivable)
export const getLatestFollowUps = () => api.get('/invoices/follow-ups/latest');
export const getFollowUps = (target: { invoiceId?: number; openingBalanceId?: number }) => api.get('/invoices/follow-ups', { params: target });
export const addFollowUp = (data: { invoiceId?: number; openingBalanceId?: number; remarkDate: string; remark: string; nextFollowUpDate?: string }) => api.post('/invoices/follow-ups', data);
export const deleteFollowUp = (id: number) => api.delete(`/invoices/follow-ups/${id}`);
// Receivables aggregated by client + per-client Statement of Account ledger
export const getReceivablesByClient = () => api.get('/receivables/by-client');
export const getClientStatement = (clientId: number, params?: { from?: string; to?: string }) =>
  api.get(`/receivables/clients/${clientId}/statement`, { params });
export const exportInvoices = () => api.get('/invoices/export', { responseType: 'blob' });
// Detailed sales register for the accountant. Takes the same filter params as
// getInvoices so the file matches the rows currently on screen.
export const exportSalesRegister = (params?: any) =>
  api.get('/invoices/export-register', { params, responseType: 'blob' });
export const createInvoice = (data: any) => api.post('/invoices', data);
export const updateInvoice = (id: number, data: any) => api.put(`/invoices/${id}`, data);
export const recordPayment = (id: number, data: any) => api.post(`/invoices/${id}/payments`, data);
export const getInvoicePayments = (id: number) => api.get(`/invoices/${id}/payments`);
export const getAllPayments = () => api.get('/invoices/payments');
export const sendInvoiceBatchEmail = (data: {
  invoiceIds: number[]; to: string[]; cc?: string[]; subject?: string; body?: string;
  pdfs: Array<{ invoiceId: number; base64: string; filename?: string }>;
  extraAttachments?: Array<{ filename: string; contentType: string; base64: string }>;
}) => api.post('/invoices/send-batch-email', data);

// Litigations
export const listAllLitigations    = (params?: { status?: string; search?: string; stage?: string; dormant?: string; lawArea?: string; assignee?: string }) => api.get('/litigations', { params });
export const getLitigationsForTask = (taskId: number) => api.get(`/litigations/task/${taskId}`);
export const createLitigation     = (taskId: number, data: any) => api.post(`/litigations/task/${taskId}`, data);
export const updateLitigation     = (id: number, data: any) => api.put(`/litigations/${id}`, data);
export const deleteLitigation     = (id: number) => api.delete(`/litigations/${id}`);
export const addLitigationStep    = (litigationId: number, data: any) => api.post(`/litigations/${litigationId}/steps`, data);
export const updateLitigationStep = (stepId: number, data: any) => api.put(`/litigations/steps/${stepId}`, data);
export const deleteLitigationStep = (stepId: number) => api.delete(`/litigations/steps/${stepId}`);
export const uploadLitigationDocument = (litigationId: number, formData: FormData) =>
  api.post(`/litigations/${litigationId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteLitigationDocument = (docId: number) => api.delete(`/litigations/documents/${docId}`);

// Retainer schedules (Invoicing → Retainers tab)
export const listRetainerSchedules = (params?: { frequency?: string; isActive?: string; clientId?: number }) =>
  api.get('/retainer-schedules', { params });
export const createRetainerSchedule = (data: any) => api.post('/retainer-schedules', data);
export const updateRetainerSchedule = (id: number, data: any) => api.put(`/retainer-schedules/${id}`, data);
export const deleteRetainerSchedule = (id: number) => api.delete(`/retainer-schedules/${id}`);
export const toggleRetainerSchedule = (id: number) => api.post(`/retainer-schedules/${id}/toggle`);

// Bank reconciliation
export const downloadReconciliationTemplate = () =>
  api.get('/reconciliation/template', { responseType: 'blob' });
export const uploadBankStatement = (file: File, bankAccount?: string) => {
  const fd = new FormData();
  fd.append('file', file);
  if (bankAccount) fd.append('bankAccount', bankAccount);
  return api.post('/reconciliation/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const listBankStatements   = () => api.get('/reconciliation/statements');
export const getBankStatement     = (id: number) => api.get(`/reconciliation/statements/${id}`);
export const deleteBankStatement  = (id: number) => api.delete(`/reconciliation/statements/${id}`);
// Each target is EITHER a regular invoice (invoiceId) or an imported opening
// balance (openingBalanceId).
type ReconcileTarget = { invoiceId: number } | { openingBalanceId: number };
export const confirmBankEntry = (
  id: number,
  data:
    | (ReconcileTarget & { tdsAmount?: number; paymentNotes?: string })
    | { allocations: Array<ReconcileTarget & { amount: number; tdsAmount?: number; paymentNotes?: string }> },
) => api.post(`/reconciliation/entries/${id}/confirm`, data);
export const bulkConfirmBankEntries = (entryIds: number[], minScore?: number) =>
  api.post('/reconciliation/entries/bulk-confirm', { entryIds, minScore });

// Deleted Items (admin)
export const getDeletedItemsSummary = () => api.get('/deleted-items/summary');
export const listDeletedItems = (entity: string) => api.get(`/deleted-items/${entity}`);
export const restoreDeletedItem = (entity: string, id: number) =>
  api.post(`/deleted-items/${entity}/${id}/restore`);
export const purgeDeletedItem = (entity: string, id: number) =>
  api.delete(`/deleted-items/${entity}/${id}/purge`);
export const exportDeletedItems = (entity: string) =>
  api.get(`/deleted-items/${entity}/export`, { responseType: 'blob' });
export const ignoreBankEntry      = (id: number, reason?: string) =>
  api.post(`/reconciliation/entries/${id}/ignore`, { reason });
export const unconfirmBankEntry   = (id: number) =>
  api.post(`/reconciliation/entries/${id}/unconfirm`);

// Send-invoice-to-client flow
export const getInvoiceEmailSuggestions = (id: number) =>
  api.get<{ to: string[]; cc: string[] }>(`/invoices/${id}/email-suggestions`);
export const getInvoiceEmailHistory = (id: number) =>
  api.get(`/invoices/${id}/email-history`);
export const sendInvoiceEmail = (id: number, data: {
  to: string[]; cc?: string[]; subject?: string; body?: string;
  pdfBase64?: string; pdfFilename?: string;
  extraAttachments?: Array<{ filename: string; contentType: string; base64: string }>;
}) => api.post(`/invoices/${id}/send-email`, data);
export const deleteInvoicePayment = (paymentId: number) => api.delete(`/invoices/payments/${paymentId}`);
// source: 'invoice' | 'opening' — matches ReceiptRow.source in the Receipts tab.
export const sendReceiptEmail = (source: 'invoice' | 'opening', id: number, data: {
  to: string[]; cc?: string[]; subject?: string; body?: string;
  pdfBase64: string; pdfFilename?: string;
}) => api.post(`/invoices/receipts/${source}/${id}/send-email`, data);
// Consolidated dues reminder: one email per client covering every outstanding
// invoice (+ opening balances), with the invoice PDFs attached.
export const sendClientReminder = (clientId: number, data: {
  to: string[]; cc?: string[]; subject?: string; note?: string;
  pdfs?: Array<{ invoiceId: number; base64: string; filename?: string }>;
}) => api.post(`/invoices/reminders/client/${clientId}/send-email`, data);
export const updateInvoiceTds = (id: number, data: any) => api.put(`/invoices/${id}/tds`, data);
export const markInvoiceSent = (id: number) => api.put(`/invoices/${id}`, { status: 'SENT' });
export const cancelInvoice = (id: number) => api.put(`/invoices/${id}/cancel`);
export const markInvoiceUnpaid = (id: number) => api.put(`/invoices/${id}/mark-unpaid`);
export const deleteInvoice = (id: number) => api.delete(`/invoices/${id}`);

// Credit Notes
export const getCreditNotes = (params?: any) => api.get('/credit-notes', { params });
export const getCreditNote = (id: number) => api.get(`/credit-notes/${id}`);
export const createCreditNote = (data: any) => api.post('/credit-notes', data);
export const updateCreditNote = (id: number, data: any) => api.put(`/credit-notes/${id}`, data);
export const cancelCreditNote = (id: number) => api.post(`/credit-notes/${id}/cancel`);
export const deleteCreditNote = (id: number) => api.delete(`/credit-notes/${id}`);
// Detailed credit-note register — mirrors the sales register's column layout.
export const exportCreditNoteRegister = (params?: any) =>
  api.get('/credit-notes/export-register', { params, responseType: 'blob' });
export const getCreditNoteSettings = () => api.get('/credit-notes/settings');
export const updateCreditNoteSettings = (data: any) => api.put('/credit-notes/settings', data);

// TDS Certificates
export const getTdsCertificates = (params?: any) => api.get('/tds', { params });
export const createTdsCertificate = (data: any) => api.post('/tds', data);
export const updateTdsCertificate = (id: number, data: any) => api.put(`/tds/${id}`, data);
export const deleteTdsCertificate = (id: number) => api.delete(`/tds/${id}`);
export const getTdsReconciliation = (params?: any) => api.get('/tds/reconciliation', { params });

// Leaves
export const getLeaves = (params?: any) => api.get('/leaves', { params });
export const getLeaveBalance = (staffId: number) => api.get(`/leaves/balance/${staffId}`);
export const exportLeaves = () => api.get('/leaves/export', { responseType: 'blob' });
export const applyLeave = (data: any) => api.post('/leaves', data);
export const updateLeave = (id: number, data: any) => api.put(`/leaves/${id}`, data);
export const cancelLeave = (id: number) => api.put(`/leaves/${id}/cancel`);
export const approveLeave = (id: number, data?: any) => api.put(`/leaves/${id}/approve`, data || {});
export const rejectLeave = (id: number, data: any) => api.put(`/leaves/${id}/reject`, data);

// Compensatory Leave
export const getCompLeaveRequests = (params?: any) => api.get('/leaves/comp', { params });
export const requestCompLeave = (data: any) => api.post('/leaves/comp', data);
export const approveCompLeave = (id: number) => api.put(`/leaves/comp/${id}/approve`);
export const rejectCompLeave = (id: number, data?: any) => api.put(`/leaves/comp/${id}/reject`, data);

// Attendance
export const getAttendance = (params?: any) => api.get('/attendance', { params });
export const getAllAttendance = (params?: any) => api.get('/attendance/all', { params });
export const getAttendanceSummary = (params?: any) => api.get('/attendance/summary', { params });
export const markAttendance = (data: any, photo?: Blob) => {
  if (photo) {
    const fd = new FormData();
    fd.append('photo', photo, 'selfie.jpg');
    Object.entries(data).forEach(([k, v]) => { if (v != null) fd.append(k, String(v)); });
    return api.post('/attendance', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
  return api.post('/attendance', data);
};

// Punches — multiple check-in/check-out pairs in one day (field staff).
// The direction is decided by the server from what is already recorded, so the
// caller never says whether this is an in or an out.
export const getPunches = (params?: { staffId?: number; date?: string }) =>
  api.get('/attendance/punches', { params });

export const recordPunch = (
  data: { latitude?: number; longitude?: number; locationAccuracy?: number; notes?: string },
  photo?: Blob,
) => {
  const fd = new FormData();
  if (photo) fd.append('photo', photo, 'punch.jpg');
  Object.entries(data).forEach(([k, v]) => { if (v != null) fd.append(k, String(v)); });
  return api.post('/attendance/punches', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Attendance Correction Requests
export const markCheckOut = () => api.post('/attendance/checkout');
export const getCorrectionRequests = () => api.get('/attendance/corrections');
export const requestAttendanceCorrection = (data: any) => api.post('/attendance/corrections', data);
export const approveAttendanceCorrection = (id: number) => api.put(`/attendance/corrections/${id}/approve`);
export const rejectAttendanceCorrection = (id: number, data?: any) => api.put(`/attendance/corrections/${id}/reject`, data);

// Notifications
export const getNotifications = () => api.get('/notifications');
export const markNotificationRead = (id: number) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');
export const deleteNotification = (id: number) => api.delete(`/notifications/${id}`);

// Personal Rules Engine
export const getRules = () => api.get('/rules');
export const getRuleOptions = () => api.get('/rules/options');
export const createRule = (data: any) => api.post('/rules', data);
export const updateRule = (id: number, data: any) => api.put(`/rules/${id}`, data);
export const deleteRule = (id: number) => api.delete(`/rules/${id}`);

// Reports (extended)
export const getMonthlyRevenue = () => api.get('/reports/monthly-revenue');
export const getClientFees = (params?: any) => api.get('/reports/client-fees', { params });
export const getStaffKPIs = (params?: any) => api.get('/reports/staff-kpis', { params });

// Timesheet Locks
export const getTimesheetLocks = () => api.get('/timesheet-locks');
export const lockWeek = (weekStart: string) => api.post('/timesheet-locks', { weekStart });
export const unlockWeek = (weekStart: string) => api.delete(`/timesheet-locks/${weekStart}`);

// Task Templates
export const getTaskTemplates = () => api.get('/task-templates');
export const createTaskTemplate = (data: any) => api.post('/task-templates', data);
export const updateTaskTemplate = (id: number, data: any) => api.put(`/task-templates/${id}`, data);
export const deleteTaskTemplate = (id: number) => api.delete(`/task-templates/${id}`);

// Bulk Task Operations
export const bulkUpdateTasks = (data: { taskIds: number[]; action: string; staffId?: number }) =>
  api.post('/tasks/bulk-update', data);

// Staff Events (birthdays + anniversaries)
export const getUpcomingEvents = () => api.get('/staff/events/upcoming');

// Client Health Scores
export const getClientHealthScores = () => api.get('/staff/clients/health');

// OCR
export const extractReceiptOCR = (formData: FormData) =>
  api.post('/ocr/extract', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Holidays
export const getHolidays = (year?: number) => api.get('/holidays', { params: year ? { year } : {} });
export const getHolidayDates = (year?: number) => api.get('/holidays/dates', { params: year ? { year } : {} });
export const createHoliday = (data: any) => api.post('/holidays', data);
export const updateHoliday = (id: number, data: any) => api.put(`/holidays/${id}`, data);
export const deleteHoliday = (id: number) => api.delete(`/holidays/${id}`);

// Announcements
export const getAnnouncements = () => api.get('/announcements');
export const createAnnouncement = (data: any) => api.post('/announcements', data);
export const updateAnnouncement = (id: number, data: any) => api.put(`/announcements/${id}`, data);
export const deleteAnnouncement = (id: number) => api.delete(`/announcements/${id}`);

// Staff Documents
export const getStaffDocuments = (params?: any) => api.get('/staff-documents', { params });
export const getExpiryAlerts = () => api.get('/staff-documents/expiry-alerts');
export const uploadStaffDocument = (formData: FormData) =>
  api.post('/staff-documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateStaffDocumentMeta = (id: number, data: any) => api.put(`/staff-documents/${id}`, data);
export const deleteStaffDocument = (id: number) => api.delete(`/staff-documents/${id}`);

// WIP Report
export const getWIPReport = () => api.get('/reports/wip');

// Tenders
export const getTenders = () => api.get('/tenders');
export const getTenderStats = () => api.get('/tenders/stats');
export const getTender = (id: number) => api.get(`/tenders/${id}`);
export const createTender = (data: any) => api.post('/tenders', data);
export const updateTender = (id: number, data: any) => api.put(`/tenders/${id}`, data);
export const deleteTender = (id: number) => api.delete(`/tenders/${id}`);
export const uploadTenderDocument = (id: number, formData: FormData) =>
  api.post(`/tenders/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteTenderDocument = (id: number, docId: number) =>
  api.delete(`/tenders/${id}/documents/${docId}`);
export const uploadTenderSubmissionFile = (id: number, formData: FormData) =>
  api.post(`/tenders/${id}/submission-files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteTenderSubmissionFile = (id: number, fileId: number) =>
  api.delete(`/tenders/${id}/submission-files/${fileId}`);
export const addTenderComment = (id: number, data: any) => api.post(`/tenders/${id}/comments`, data);
export const deleteTenderComment = (id: number, commentId: number) =>
  api.delete(`/tenders/${id}/comments/${commentId}`);

// Task Archive / Freeze
export const confirmTaskArchive = (id: number) => api.post(`/tasks/${id}/confirm-archive`);
export const freezeTask = (id: number) => api.post(`/tasks/${id}/freeze`);
export const unfreezeTask = (id: number) => api.post(`/tasks/${id}/unfreeze`);

// Leads
export const getLeads = (params?: any) => api.get('/leads', { params });
export const getLead = (id: number) => api.get(`/leads/${id}`);
export const getLeadStats = () => api.get('/leads/stats');
export const createLead = (data: any) => api.post('/leads', data);
export const updateLead = (id: number, data: any) => api.put(`/leads/${id}`, data);
export const deleteLead = (id: number) => api.delete(`/leads/${id}`);
export const addLeadNote = (id: number, data: any) => api.post(`/leads/${id}/notes`, data);
export const deleteLeadNote = (noteId: number) => api.delete(`/leads/notes/${noteId}`);
export const convertLeadToClient = (id: number, data: any) => api.post(`/leads/${id}/convert`, data);

// GST Lookup
export const getGstStatus = () => api.get('/gst/status');
export const lookupGstin = (gstin: string) => api.get(`/gst/lookup/${gstin}`);

// Opening Balances (pre-software receivables)
export const getOpeningBalances = (params?: any) => api.get('/opening-balances', { params });
export const getOpeningBalanceSummary = () => api.get('/opening-balances/summary');
export const createOpeningBalance = (data: any) => api.post('/opening-balances', data);
export const updateOpeningBalance = (id: number, data: any) => api.put(`/opening-balances/${id}`, data);
export const deleteOpeningBalance = (id: number) => api.delete(`/opening-balances/${id}`);
export const recordOpeningBalancePayment = (id: number, data: any) => api.post(`/opening-balances/${id}/payments`, data);
export const importOpeningBalances = (file: File) => {
  const fd = new FormData(); fd.append('file', file);
  return api.post('/opening-balances/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const downloadOpeningBalanceTemplate = () => api.get('/opening-balances/import-template', { responseType: 'blob' });

// Receivable Adjustments (write-offs: Short & Excess / Bad Debt)
export const getAdjustments = (params?: any) => api.get('/adjustments', { params });
export const createAdjustment = (data: any) => api.post('/adjustments', data);
export const reverseAdjustment = (id: number) => api.put(`/adjustments/${id}/reverse`);

// Client GSTINs
export const getClientGstins = (clientId: number) => api.get(`/clients/${clientId}/gstins`);
export const createClientGstin = (clientId: number, data: any) => api.post(`/clients/${clientId}/gstins`, data);
export const updateClientGstin = (clientId: number, id: number, data: any) => api.put(`/clients/${clientId}/gstins/${id}`, data);
export const deleteClientGstin = (clientId: number, id: number) => api.delete(`/clients/${clientId}/gstins/${id}`);

// IT Assets
export const getITAssets = () => api.get('/it-assets');
export const getITAsset = (id: number) => api.get(`/it-assets/${id}`);
export const createITAsset = (data: any) => api.post('/it-assets', data);
export const updateITAsset = (id: number, data: any) => api.put(`/it-assets/${id}`, data);
export const deleteITAsset = (id: number) => api.delete(`/it-assets/${id}`);
export const assignITAsset = (id: number, assignedToId: number) => api.post(`/it-assets/${id}/assign`, { assignedToId });
export const returnITAsset = (id: number) => api.post(`/it-assets/${id}/return`);
export const closeITAsset = (id: number, data: { endStatus: 'SOLD' | 'DISPOSED'; endDate?: string; endAmount?: number }) =>
  api.post(`/it-assets/${id}/close`, data);
export const createAssetServiceLog = (id: number, data: any) => api.post(`/it-assets/${id}/service-logs`, data);
export const deleteAssetServiceLog = (id: number) => api.delete(`/it-assets/service-logs/${id}`);
export const deleteAssetAttachment = (id: number) => api.delete(`/it-assets/attachments/${id}`);

export const getInvoiceSettings = () => api.get('/invoices/settings');
export const updateInvoiceSettings = (data: any) => api.put('/invoices/settings', data);
export const getWizardBillableTasks = (params: { year: number; month: number; billingEntityId?: number; clientId?: number; profitCentreId?: number }) =>
  api.get('/invoices/wizard/billable', { params });
export const wizardCreateInvoices = (data: { invoiceDate: string; dueDate?: string; defaultBillingEntityId?: number; items: any[] }) =>
  api.post('/invoices/wizard/create', data);
export const getWizardPendingRetainers = (fy: number) =>
  api.get('/invoices/wizard/pending-retainers', { params: { fy } });
export const skipRetainerPeriod = (data: { scheduleId: number; periodStart: string; reason?: string }) =>
  api.post('/invoices/wizard/skip-retainer', data);

// Sub-Tasks
export const getAllSubTasks = () => api.get('/subtasks');
export const getSubTasks = (taskId: number) => api.get(`/tasks/${taskId}/subtasks`);
export const createSubTask = (taskId: number, data: any) => api.post(`/tasks/${taskId}/subtasks`, data);
export const updateSubTask = (id: number, data: any) => api.put(`/subtasks/${id}`, data);
export const closeSubTask = (id: number) => api.put(`/subtasks/${id}/close`);
export const deleteSubTask = (id: number) => api.delete(`/subtasks/${id}`);

// Task Comments
export const getTaskComments = (taskId: number) => api.get(`/tasks/${taskId}/comments`);
export const createTaskComment = (taskId: number, data: any) => api.post(`/tasks/${taskId}/comments`, data);
export const deleteTaskComment = (commentId: number) => api.delete(`/tasks/comments/${commentId}`);

// Client Document Vault
export const getClientDocuments = (clientId: number) => api.get(`/clients/${clientId}/vault`);
export const uploadClientDocument = (clientId: number, formData: FormData) =>
  api.post(`/clients/${clientId}/vault`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteClientDocument = (docId: number) => api.delete(`/clients/vault/${docId}`);

// Reports — billing summary + due alerts
export const getBillingSummary = (params?: any) => api.get('/reports/billing-summary', { params });
export const triggerDueAlerts = () => api.post('/reports/trigger-due-alerts');

// Chat
export const getChatRooms = () => api.get('/chat/rooms');
export const getChatStaff = () => api.get('/chat/staff');
export const getChatUnreadCount = () => api.get('/chat/unread-count');
export const getOrCreateDM = (targetStaffId: number) => api.post('/chat/rooms/dm', { targetStaffId });
export const getChatMessages = (roomId: number, params?: { before?: number; after?: number }) =>
  api.get(`/chat/rooms/${roomId}/messages`, { params });
export const sendChatMessage = (roomId: number, content: string) =>
  api.post(`/chat/rooms/${roomId}/messages`, { content });
export const sendChatFile = (roomId: number, formData: FormData) =>
  api.post(`/chat/rooms/${roomId}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const markChatRoomRead = (roomId: number) => api.put(`/chat/rooms/${roomId}/read`);
export const searchChatRefs = (q: string) => api.get('/chat/search-refs', { params: { q } });

// Org Settings
export const getOrgSettings = () => api.get("/org-settings");
export const updateOrgSettings = (data: any) => api.put("/org-settings", data);
export const restoreOrgDefaults = (categories?: string[]) => api.post("/org-settings/restore-defaults", { categories });
export const sendTestEmail = (to: string) => api.post("/org-settings/test-email", { to });

// Payroll
export const getSalaryHistory = (staffId: number) => api.get(`/payroll/staff/${staffId}/salary`);
export const addSalaryRecord = (staffId: number, data: { effectiveFrom: string; monthlyGross: number; monthlyTds?: number; notes?: string }) =>
  api.post(`/payroll/staff/${staffId}/salary`, data);
export const deleteSalaryRecord = (id: number) => api.delete(`/payroll/salary/${id}`);

export const getLeaveAdjustments = (staffId: number, year?: number) =>
  api.get(`/payroll/staff/${staffId}/leave-adjustments`, { params: year ? { year } : {} });
export const addLeaveAdjustment = (staffId: number, data: { year: number; days: number; reason: string; notes?: string }) =>
  api.post(`/payroll/staff/${staffId}/leave-adjustments`, data);
export const deleteLeaveAdjustment = (id: number) => api.delete(`/payroll/leave-adjustments/${id}`);

export const listPayrollRuns = () => api.get('/payroll/runs');
export const generatePayrollRun = (month: number, year: number) => api.post('/payroll/runs', { month, year });
export const getPayrollRun = (id: number) => api.get(`/payroll/runs/${id}`);
export const updatePayslip = (runId: number, payslipId: number, data: { lopDays?: number; adjustments?: { label: string; amount: number }[]; remarks?: string }) =>
  api.patch(`/payroll/runs/${runId}/payslips/${payslipId}`, data);
export const finalizePayrollRun = (id: number) => api.post(`/payroll/runs/${id}/finalize`);
export const getMyPayslips = () => api.get('/payroll/my-payslips');

// Payments (HR/Admin): batches of salary / reimbursement / other payments → bank files
export const listPaymentBatches = () => api.get('/payments/batches');
export const createPaymentBatch = (data: { paymentDate: string; title?: string }) => api.post('/payments/batches', data);
export const getPaymentBatch = (id: number) => api.get(`/payments/batches/${id}`);
export const updatePaymentBatch = (id: number, data: { paymentDate?: string; title?: string }) => api.patch(`/payments/batches/${id}`, data);
export const deletePaymentBatch = (id: number) => api.delete(`/payments/batches/${id}`);
export const addPaymentSalaries = (id: number, runId: number) => api.post(`/payments/batches/${id}/salaries`, { runId });
export const getReimbursementCandidates = () => api.get('/payments/reimbursement-candidates');
export const addPaymentReimbursements = (id: number, ids?: number[]) => api.post(`/payments/batches/${id}/reimbursements`, { ids });
export const addPaymentOther = (id: number, data: any) => api.post(`/payments/batches/${id}/lines`, data);
export const updatePaymentLine = (lineId: number, data: { amount?: number; narration?: string; entityId?: number | null }) =>
  api.patch(`/payments/lines/${lineId}`, data);
export const deletePaymentLine = (lineId: number) => api.delete(`/payments/lines/${lineId}`);
export const exportPaymentBatch = (id: number, entityId: number) =>
  api.get(`/payments/batches/${id}/export`, { params: { entityId }, responseType: 'blob' });
export const markPaymentBatchPaid = (id: number) => api.post(`/payments/batches/${id}/mark-paid`);
export const listPayees = () => api.get('/payments/payees');
export const createPayee = (data: any) => api.post('/payments/payees', data);
export const updatePayee = (id: number, data: any) => api.put(`/payments/payees/${id}`, data);
export const deletePayee = (id: number) => api.delete(`/payments/payees/${id}`);
